<template>
  <div class="th-batch-chart">
    <div v-if="hasData" class="th-batch-chart__toolbar">
      <p class="th-batch-chart__hint">
        Arraste no gráfico para ampliar · Scroll: zoom · Shift+arraste: mover
      </p>
      <button
        v-if="canResetZoom"
        type="button"
        class="th-batch-chart__reset"
        @click="resetZoom"
      >
        Redefinir zoom
      </button>
    </div>
    <div ref="chartWrap" class="th-batch-chart__canvas">
      <canvas ref="canvas"></canvas>
      <div
        v-show="selectionRect.visible"
        class="th-batch-chart__selection"
        :style="selectionRect.style"
      />
    </div>
  </div>
</template>

<script>
import Chart from "chart.js"
import { Line } from "vue-chartjs"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"

export default {
  name: "TempHumidityBatchChart",
  extends: Line,
  props: {
    datasets: {
      type: Array,
      default: () => [],
    },
    chartKey: {
      type: Number,
      default: 0,
    },
    metric: {
      type: String,
      default: "temperature",
      validator: (v) => v === "temperature" || v === "humidity",
    },
  },
  data() {
    return {
      chartData: { datasets: [] },
      options: {},
      canResetZoom: false,
      initialScaleLimits: null,
      selectionRect: { visible: false, style: {} },
    }
  },
  computed: {
    hasData() {
      return Array.isArray(this.datasets) && this.datasets.length > 0
    },
  },
  watch: {
    datasets: {
      handler() {
        this.canResetZoom = false
        this.$nextTick(() => this.updateChart())
      },
      deep: true,
    },
    chartKey() {
      this.canResetZoom = false
      this.$nextTick(() => this.updateChart())
    },
    metric() {
      this.canResetZoom = false
      this.$nextTick(() => this.updateChart())
    },
  },
  mounted() {
    this.updateChart()
  },
  beforeDestroy() {
    this.teardownCustomZoom()
    if (this.$data._chart) {
      this.$data._chart.destroy()
      this.$data._chart = null
    }
  },
  methods: {
    collectXExtents(datasets) {
      const values = []
      for (const ds of datasets || []) {
        for (const pt of ds.data || []) {
          const x = pt?.x != null ? Number(pt.x) : NaN
          if (!Number.isNaN(x)) values.push(x)
        }
      }
      if (!values.length) return { min: undefined, max: undefined }
      return { min: Math.min(...values), max: Math.max(...values) }
    },
    computeTimeStepMs(minMs, maxMs, targetTicks = 6) {
      const span = Math.max(maxMs - minMs, 60 * 1000)
      const raw = span / targetTicks
      const steps = [
        5 * 60 * 1000,
        10 * 60 * 1000,
        15 * 60 * 1000,
        30 * 60 * 1000,
        60 * 60 * 1000,
        2 * 60 * 60 * 1000,
        3 * 60 * 60 * 1000,
        6 * 60 * 60 * 1000,
        12 * 60 * 60 * 1000,
        24 * 60 * 60 * 1000,
      ]
      for (const step of steps) {
        if (step >= raw * 0.85) return step
      }
      return steps[steps.length - 1]
    },
    formatTimeTick(value, stepMs) {
      try {
        const d = new Date(value)
        if (stepMs >= 24 * 60 * 60 * 1000) return format(d, "dd/MM", { locale: ptBR })
        return format(d, "dd/MM HH:mm", { locale: ptBR })
      } catch {
        return String(value)
      }
    },
    buildXAxisConfig(datasets) {
      const { min, max } = this.collectXExtents(datasets)
      if (min == null || max == null) {
        return { type: "linear", ticks: { maxTicksLimit: 8 } }
      }
      const pad = Math.max((max - min) * 0.02, 2 * 60 * 1000)
      const step = this.computeTimeStepMs(min - pad, max + pad)
      const tickMin = Math.floor((min - pad) / step) * step
      const tickMax = Math.ceil((max + pad) / step) * step
      return {
        type: "linear",
        stepMs: step,
        tickMin,
        tickMax,
        ticks: {
          min: tickMin,
          max: tickMax,
          stepSize: step,
          maxTicksLimit: 10,
          callback: (v) => this.formatTimeTick(v, step),
        },
      }
    },
    yAxisTicksConfig() {
      const isHum = this.metric === "humidity"
      if (isHum) {
        return {
          min: 0,
          max: 100,
          stepSize: 20,
          format: (v) => `${Math.round(Number(v))}`,
        }
      }
      return {
        min: 0,
        max: 50,
        stepSize: 5,
        format: (v) => Number(v).toFixed(0),
      }
    },
    buildChartOptions(datasets) {
      const isHum = this.metric === "humidity"
      const grid = isHum ? "rgba(22, 138, 173, 0.12)" : "rgba(120, 184, 67, 0.12)"
      const titleText = isHum ? "Umidade (% UR) × tempo" : "Temperatura (ºC) × tempo"
      const yLabel = isHum ? "Umidade (% UR)" : "Temperatura (ºC)"
      const yTicks = this.yAxisTicksConfig()
      const xAxis = this.buildXAxisConfig(datasets)
      const self = this

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        pan: {
          enabled: true,
          mode: "xy",
          modifierKey: "shift",
          onPanComplete() {
            self.canResetZoom = true
          },
        },
        zoom: {
          enabled: true,
          mode: "xy",
          drag: true,
          speed: 0.1,
          sensitivity: 3,
          onZoomComplete() {
            self.canResetZoom = true
          },
          rangeMin: { y: 0 },
          rangeMax: { y: isHum ? 100 : 50 },
        },
        layout: { padding: { bottom: 8, top: 4 } },
        title: {
          display: true,
          text: titleText,
          fontSize: 16,
          fontColor: "#1b4238",
        },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            boxWidth: 20,
            padding: 18,
            fontSize: 15,
            fontStyle: "bold",
            fontColor: "#1b4238",
          },
        },
        tooltips: {
          mode: "nearest",
          intersect: false,
          callbacks: {
            title(items) {
              if (!items.length) return ""
              try {
                return format(new Date(items[0].xLabel), "dd/MM/yyyy HH:mm", { locale: ptBR })
              } catch {
                return String(items[0].xLabel)
              }
            },
            label(item, data) {
              const ds = data.datasets[item.datasetIndex]
              const pt = ds.data[item.index]
              const yVal = Number(item.yLabel)
              if (Number.isNaN(yVal)) return `${ds.label}: —`
              if (isHum) {
                const extra =
                  pt?.temp != null && !Number.isNaN(Number(pt.temp))
                    ? ` | ${Number(pt.temp).toFixed(1)} ºC`
                    : ""
                return `${ds.label}: ${yVal.toFixed(1)}% UR${extra}`
              }
              const humExtra =
                pt?.hum != null && !Number.isNaN(Number(pt.hum))
                  ? ` | UR ${Number(pt.hum).toFixed(1)}%`
                  : ""
              return `${ds.label}: ${yVal.toFixed(1)} ºC${humExtra}`
            },
          },
        },
        scales: {
          xAxes: [
            {
              type: xAxis.type,
              position: "bottom",
              scaleLabel: {
                display: true,
                labelString: "Tempo",
                fontColor: "#3e3e3e",
              },
              ticks: {
                fontColor: "#666",
                ...xAxis.ticks,
              },
              gridLines: { color: grid },
            },
          ],
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: yLabel,
                fontColor: "#3e3e3e",
              },
              ticks: {
                fontColor: "#666",
                min: yTicks.min,
                max: yTicks.max,
                stepSize: yTicks.stepSize,
                maxTicksLimit: isHum ? 6 : 12,
                callback: (v) => (v != null ? yTicks.format(v) : v),
              },
              gridLines: { color: grid },
            },
          ],
        },
      }
    },
    getScaleLimits(chart) {
      if (!chart?.scales) return null
      const xKey = Object.keys(chart.scales).find((k) => chart.scales[k].isHorizontal())
      const yKey = Object.keys(chart.scales).find((k) => !chart.scales[k].isHorizontal())
      if (!xKey || !yKey) return null
      const xScale = chart.scales[xKey]
      const yScale = chart.scales[yKey]
      return {
        xKey,
        yKey,
        x: {
          min: xScale.options.ticks.min,
          max: xScale.options.ticks.max,
          stepSize: xScale.options.ticks.stepSize,
        },
        y: {
          min: yScale.options.ticks.min,
          max: yScale.options.ticks.max,
          stepSize: yScale.options.ticks.stepSize,
        },
      }
    },
    applyScaleLimits(chart, limits) {
      if (!chart || !limits) return
      const x = chart.scales[limits.xKey]
      const y = chart.scales[limits.yKey]
      if (x?.options?.ticks) {
        x.options.ticks.min = limits.x.min
        x.options.ticks.max = limits.x.max
        if (limits.x.stepSize != null) x.options.ticks.stepSize = limits.x.stepSize
      }
      if (y?.options?.ticks) {
        y.options.ticks.min = limits.y.min
        y.options.ticks.max = limits.y.max
        if (limits.y.stepSize != null) y.options.ticks.stepSize = limits.y.stepSize
      }
      chart.update(0)
    },
    captureInitialScaleLimits(chart) {
      this.initialScaleLimits = this.getScaleLimits(chart)
    },
    renderChartInstance(data, options) {
      if (this.$data._chart) {
        this.$data._chart.destroy()
        this.$data._chart = null
      }
      if (!this.$refs.canvas) return null
      const chart = new Chart(this.$refs.canvas.getContext("2d"), {
        type: "line",
        data,
        options,
      })
      this.$data._chart = chart
      return chart
    },
    teardownCustomZoom() {
      const h = this._customZoomHandlers
      if (!h?.canvas) return
      const { canvas, onDown, onMove, onUp, onWheel } = h
      canvas.removeEventListener("mousedown", onDown)
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
      canvas.removeEventListener("wheel", onWheel)
      this._customZoomHandlers = null
    },
    setupCustomZoom(chart) {
      this.teardownCustomZoom()
      if (!chart?.ctx?.canvas) return

      const canvas = chart.ctx.canvas
      const isHum = this.metric === "humidity"
      let dragStart = null
      let panLast = null

      const getXY = (e) => {
        const rect = canvas.getBoundingClientRect()
        return { x: e.clientX - rect.left, y: e.clientY - rect.top }
      }

      const pixelToValues = (px, py) => {
        const xScale = Object.values(chart.scales).find((s) => s.isHorizontal())
        const yScale = Object.values(chart.scales).find((s) => !s.isHorizontal())
        if (!xScale || !yScale) return null
        return {
          x: xScale.getValueForPixel(px),
          y: yScale.getValueForPixel(py),
        }
      }

      const applyBoxZoom = (x1, y1, x2, y2) => {
        const v1 = pixelToValues(x1, y1)
        const v2 = pixelToValues(x2, y2)
        if (!v1 || !v2) return
        let xMin = Math.min(v1.x, v2.x)
        let xMax = Math.max(v1.x, v2.x)
        let yMin = Math.min(v1.y, v2.y)
        let yMax = Math.max(v1.y, v2.y)
        if (xMax - xMin < 60000) return
        if (yMax - yMin < 0.5) return

        yMin = Math.max(0, yMin)
        yMax = Math.min(isHum ? 100 : 50, yMax)

        const limits = this.getScaleLimits(chart)
        if (!limits) return
        limits.x.min = xMin
        limits.x.max = xMax
        limits.y.min = yMin
        limits.y.max = yMax
        delete limits.x.stepSize
        delete limits.y.stepSize
        this.applyScaleLimits(chart, limits)
        this.canResetZoom = true
      }

      const onDown = (e) => {
        if (e.button !== 0) return
        const pt = getXY(e)
        if (e.shiftKey) {
          panLast = pt
          return
        }
        dragStart = pt
      }

      const onMove = (e) => {
        if (panLast && e.shiftKey) {
          const pt = getXY(e)
          const dx = pt.x - panLast.x
          const dy = pt.y - panLast.y
          panLast = pt
          const limits = this.getScaleLimits(chart)
          if (!limits) return
          const xScale = chart.scales[limits.xKey]
          const yScale = chart.scales[limits.yKey]
          const xRange = limits.x.max - limits.x.min
          const yRange = limits.y.max - limits.y.min
          const xShift = (-dx / xScale.width) * xRange
          const yShift = (dy / yScale.height) * yRange
          limits.x.min += xShift
          limits.x.max += xShift
          limits.y.min += yShift
          limits.y.max += yShift
          limits.y.min = Math.max(0, limits.y.min)
          limits.y.max = Math.min(isHum ? 100 : 50, limits.y.max)
          this.applyScaleLimits(chart, limits)
          this.canResetZoom = true
          return
        }
        if (!dragStart) return
        const pt = getXY(e)
        const x = Math.min(dragStart.x, pt.x)
        const y = Math.min(dragStart.y, pt.y)
        const w = Math.abs(pt.x - dragStart.x)
        const h = Math.abs(pt.y - dragStart.y)
        this.selectionRect = {
          visible: true,
          style: {
            left: `${x}px`,
            top: `${y}px`,
            width: `${w}px`,
            height: `${h}px`,
          },
        }
      }

      const onUp = (e) => {
        this.selectionRect = { visible: false, style: {} }
        if (panLast) {
          panLast = null
          return
        }
        if (!dragStart) return
        const end = getXY(e)
        applyBoxZoom(dragStart.x, dragStart.y, end.x, end.y)
        dragStart = null
      }

      const onWheel = (e) => {
        if (!e.cancelable) return
        e.preventDefault()
        const limits = this.getScaleLimits(chart)
        if (!limits) return
        const pt = getXY(e)
        const factor = e.deltaY < 0 ? 0.85 : 1.18
        const zoomAround = (min, max, center) => {
          const range = max - min
          const newRange = range * factor
          const rel = range > 0 ? (center - min) / range : 0.5
          return {
            min: center - newRange * rel,
            max: center + newRange * (1 - rel),
          }
        }
        const vals = pixelToValues(pt.x, pt.y)
        if (!vals) return
        const nx = zoomAround(limits.x.min, limits.x.max, vals.x)
        const ny = zoomAround(limits.y.min, limits.y.max, vals.y)
        limits.x.min = nx.min
        limits.x.max = nx.max
        limits.y.min = Math.max(0, ny.min)
        limits.y.max = Math.min(isHum ? 100 : 50, ny.max)
        delete limits.x.stepSize
        delete limits.y.stepSize
        this.applyScaleLimits(chart, limits)
        this.canResetZoom = true
      }

      canvas.addEventListener("mousedown", onDown)
      window.addEventListener("mousemove", onMove)
      window.addEventListener("mouseup", onUp)
      canvas.addEventListener("wheel", onWheel, { passive: false })

      this._customZoomHandlers = { canvas, onDown, onMove, onUp, onWheel }
    },
    resetZoom() {
      const chart = this.$data._chart
      if (chart && typeof chart.resetZoom === "function") {
        try {
          chart.resetZoom()
        } catch {
          /* fallback abaixo */
        }
      }
      if (chart && this.initialScaleLimits) {
        this.applyScaleLimits(chart, JSON.parse(JSON.stringify(this.initialScaleLimits)))
      }
      this.canResetZoom = false
    },
    updateChart() {
      const ds = Array.isArray(this.datasets) ? this.datasets : []
      this.teardownCustomZoom()
      this.chartData = { datasets: ds }
      this.options = this.buildChartOptions(ds)
      if (!ds.length) {
        if (this.$data._chart) {
          this.$data._chart.destroy()
          this.$data._chart = null
        }
        return
      }
      const chart = this.renderChartInstance(this.chartData, this.options)
      if (!chart) return
      this.$nextTick(() => {
        this.captureInitialScaleLimits(chart)
        this.setupCustomZoom(chart)
      })
    },
  },
}
</script>

<style scoped>
.th-batch-chart {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.th-batch-chart__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  margin-bottom: 6px;
  flex-shrink: 0;
}

.th-batch-chart__hint {
  margin: 0;
  font-size: 11px;
  color: #5a6b62;
  line-height: 1.35;
}

.th-batch-chart__reset {
  flex-shrink: 0;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #3e3e3e;
  background: #fff;
  border: 1px solid #78b843;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.th-batch-chart__reset:hover {
  background: #78b843;
  color: #fff;
}

.th-batch-chart__canvas {
  position: relative;
  flex: 1;
  min-height: 200px;
  cursor: crosshair;
}

.th-batch-chart__canvas canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.th-batch-chart__selection {
  position: absolute;
  border: 1px solid rgba(27, 66, 56, 0.65);
  background: rgba(120, 184, 67, 0.18);
  pointer-events: none;
  z-index: 2;
}
</style>
